import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const PHASE_OPTIONS = [
  { value: 0, label: 'Phase 0 – Not Started' },
  { value: 1, label: 'Phase 1 – Daily Reviews' },
  { value: 2, label: 'Phase 2 – Gradual Increase' },
  { value: 3, label: 'Phase 3 – Weekly Reviews' },
  { value: 4, label: 'Phase 4 – Weekly to Monthly' },
  { value: 5, label: 'Phase 5 – Monthly Reviews' },
  { value: 6, label: 'Phase 6 – Monthly to Quarterly' },
  { value: 7, label: 'Phase 7 – Quarterly Maintenance' },
];

const FilterPanel = ({ 
  filters, 
  onFiltersChange, 
  onClearFilters, 
  isCollapsed, 
  onToggleCollapse,
  showPhaseFilter = false,
  className = ''
}) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const bibleVersionOptions = [
    { value: 'all', label: 'All Versions' },
    { value: 'kjv', label: 'King James Version (KJV)' },
    { value: 'niv', label: 'New International Version (NIV)' },
    { value: 'esv', label: 'English Standard Version (ESV)' },
    { value: 'nlt', label: 'New Living Translation (NLT)' },
    { value: 'nasb', label: 'New American Standard Bible (NASB)' }
  ];

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'faith', label: 'Faith' },
    { value: 'love', label: 'Love' },
    { value: 'hope', label: 'Hope' },
    { value: 'peace', label: 'Peace' },
    { value: 'strength', label: 'Strength' },
    { value: 'wisdom', label: 'Wisdom' }
  ];

  // selectedPhases is an array of phase numbers (or 'all' string for legacy)
  const selectedPhases = Array.isArray(filters?.phases) ? filters?.phases : [];

  const handleFilterChange = (filterType, value) => {
    const updated = { ...filters, [filterType]: value };
    setLocalFilters(updated);
    onFiltersChange(updated);
  };

  const handlePhaseCheckbox = (phaseValue, checked) => {
    let newPhases;
    if (checked) {
      newPhases = [...selectedPhases, phaseValue];
    } else {
      newPhases = selectedPhases?.filter(p => p !== phaseValue);
    }
    const updated = { ...filters, phases: newPhases, phase: newPhases?.length === 0 ? 'all' : 'multi' };
    setLocalFilters(updated);
    onFiltersChange(updated);
  };

  const handleClearAll = () => {
    onClearFilters();
  };

  if (isCollapsed) {
    return (
      <div className={`bg-card border border-border rounded-lg p-4 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Filter" size={18} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Filters</span>
            <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md font-mono">
              {Object.values(localFilters)?.filter(v => v !== 'all' && v !== false && !(Array.isArray(v) && v?.length === 0))?.length}
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleCollapse}
          >
            <Icon name="ChevronDown" size={16} />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={18} className="text-muted-foreground" />
          <h3 className="text-lg font-heading font-semibold text-foreground">Filters</h3>
          <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md font-mono">
            {Object.values(localFilters)?.filter(v => v !== 'all' && v !== false && !(Array.isArray(v) && v?.length === 0))?.length} active
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
          >
            Clear All
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleCollapse}
            className="md:hidden"
          >
            <Icon name="ChevronUp" size={16} />
          </Button>
        </div>
      </div>

      {/* Filter Content */}
      <div className="space-y-4 pt-4">
        {/* Bible Version */}
        <div>
          <Select
            label="Bible Version"
            options={bibleVersionOptions}
            value={localFilters?.version}
            onChange={(value) => handleFilterChange('version', value)}
          />
        </div>

        {/* Memorization Phase – Checkboxes */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-2">Memorization Phase</h4>
          <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
            {PHASE_OPTIONS?.map(opt => (
              <label
                key={opt?.value}
                className="flex items-center gap-2 cursor-pointer group"
              >
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
                  checked={selectedPhases?.includes(opt?.value)}
                  onChange={(e) => handlePhaseCheckbox(opt?.value, e?.target?.checked)}
                />
                <span className="text-sm text-foreground group-hover:text-primary transition-colors">
                  {opt?.label}
                </span>
              </label>
            ))}
          </div>
          {selectedPhases?.length > 0 && (
            <button
              onClick={() => {
                const updated = { ...filters, phases: [], phase: 'all' };
                setLocalFilters(updated);
                onFiltersChange(updated);
              }}
              className="mt-2 text-xs text-primary hover:underline"
            >
              Clear phase selection
            </button>
          )}
        </div>

        {/* Category */}
        <div>
          <Select
            label="Category"
            options={categoryOptions}
            value={localFilters?.category}
            onChange={(value) => handleFilterChange('category', value)}
          />
        </div>

        {/* Additional Filters */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-foreground">Additional Filters</h4>
          
          <Checkbox
            label="Has Personal Notes"
            checked={localFilters?.hasNotes}
            onChange={(e) => handleFilterChange('hasNotes', e?.target?.checked)}
          />
          
          <Checkbox
            label="Has Linked Verses"
            checked={localFilters?.hasLinks}
            onChange={(e) => handleFilterChange('hasLinks', e?.target?.checked)}
          />
        </div>

        {/* Date Range */}
        <div>
          <Select
            label="Date Added"
            options={[
              { value: 'all', label: 'All Time' },
              { value: 'today', label: 'Today' },
              { value: 'week', label: 'This Week' },
              { value: 'month', label: 'This Month' },
              { value: 'quarter', label: 'Last 3 Months' }
            ]}
            value={localFilters?.dateRange}
            onChange={(value) => handleFilterChange('dateRange', value)}
          />
        </div>

        {/* Quick Filters */}
        <div className="pt-2 border-t border-border">
          <h4 className="text-sm font-medium text-foreground mb-2">Quick Filters</h4>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="xs"
              onClick={() => handleFilterChange('status', 'learning')}
            >
              Currently Learning
            </Button>
            <Button
              variant="outline"
              size="xs"
              onClick={() => handleFilterChange('hasNotes', true)}
            >
              With Notes
            </Button>
            <Button
              variant="outline"
              size="xs"
              onClick={() => handleFilterChange('dateRange', 'week')}
            >
              Added This Week
            </Button>
          </div>
        </div>

        {/* Enhanced Clear Filters Button */}
        <Button
          variant="outline"
          onClick={onClearFilters}
          className="w-full text-sm"
          iconName="X"
          iconPosition="left"
        >
          Clear All Filters
        </Button>
      </div>
    </div>
  );
};

export default FilterPanel;